import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../features/auth/useAuth';
import { useBusiness } from '../../features/onboarding/useBusiness';
import { apiClient } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { EntitlementsRead, PlanFeature, PlanLimitKey } from '../../types/api';
import { PlanContext, type PlanContextValue } from './planContext';

/** Account-wide, not brand-scoped: the plan lives on the owner's profile. */
function fetchEntitlements(signal?: AbortSignal): Promise<EntitlementsRead> {
  return apiClient.get<EntitlementsRead>('/api/v1/me/entitlements', { signal });
}

/** Result of the last completed read, pinned to the account it was read for. */
interface LoadedEntitlements {
  userId: string;
  entitlements: EntitlementsRead | null;
}

/**
 * Loads the account's entitlements once per sign-in and shares them with the
 * shell and every screen that asks `usePlan()`.
 *
 * A failed read is not fatal and never blocks: entitlements stay null, `can()`
 * answers true and `limit()` undefined, and the backend's 402 remains the only
 * enforcement. Re-read on `refresh()` or a `requestRefresh(_, ['entitlements'])`
 * from anywhere (checkout or portal return, a staff change seen by the user).
 */
export function PlanProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const userId = user?.id ?? null;
  const [loaded, setLoaded] = useState<LoadedEntitlements | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useRefreshOn(businessId, 'entitlements', refresh);

  useEffect(() => {
    if (!userId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const entitlements = await fetchEntitlements(controller.signal);
        if (cancelled) return;
        setLoaded({ userId, entitlements });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        // Fail soft: an unknown plan reads as unknown, not as Free.
        console.warn('Entitlements lookup failed.', getErrorMessage(caught));
        setLoaded({ userId, entitlements: null });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId, nonce]);

  const value = useMemo<PlanContextValue>(() => {
    const isCurrent = loaded !== null && loaded.userId === userId;
    const entitlements = isCurrent ? loaded.entitlements : null;
    return {
      entitlements,
      loading: userId !== null && !isCurrent,
      refresh,
      can: (feature: PlanFeature) => (entitlements ? entitlements.features[feature] : true),
      limit: (key: PlanLimitKey) => (entitlements ? entitlements.limits[key] : undefined),
    };
  }, [loaded, userId, refresh]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}
